import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowLeftIcon from "../../../../assets/svg/ArrowLeftIcon";
import InfoIcon from "../../../../assets/svg/InfoIcon.svg";
import { useAuthentication } from "../../../hooks/useAuthentication";
import { getAllApplications } from "./components/enrollmentForm/api/getAllApplications";
import Table from "./components/table/Table";
import { extractGroupsPerApplication } from "./components/table/utils";
import styles from "./EnrollmentApplications.module.css";
import {
  extractUniqueGroups,
  filterApplicationsByProgram,
  filterApplicationsBySearch,
  programOptions,
} from "./utils";

const EnrollmentApplications = () => {
  const navigate = useNavigate();
  const { authToken } = useAuthentication();
  const [selectedApplicationStatus, setSelectedApplicationStatus] =
    useState("undecided");
  const [applications, setApplications] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("3,4,5,6,7,8,9,10");
  const [updatedApplications, setUpdatedApplications] = useState([]);
  const [applicationsCount, setApplicationsCount] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const handleSelectChange = (event) => {
    setSelectedProgram(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    const defaultIds = [3, 4, 5, 6, 7, 8, 9, 10];
    const fetchApplications = async () => {
      const response = await getAllApplications(authToken);
      setApplications(response);
      const defaultData = response?.applications?.filter((application) =>
        defaultIds.includes(application.programId)
      );
      const sortedData = defaultData?.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setUpdatedApplications(sortedData);
    };
    if (authToken) fetchApplications();
  }, [authToken]);

  useEffect(() => {
    const filteredByProgram = filterApplicationsByProgram(
      selectedProgram,
      applications
    );
    const filteredBySearch = filterApplicationsBySearch(
      searchQuery,
      filteredByProgram
    );
    const grupa = extractGroupsPerApplication(filteredBySearch);
    const currentUsersWithGroups = filteredBySearch.map((user) => {
      const userGroup = grupa.find(
        (groupData) => groupData.applicationId === user.id
      );
      return {
        ...user,
        validGroup: userGroup ? userGroup.group : null,
        hadGroup: Boolean(userGroup),
      };
    });

    const filteredByGroupArchive = currentUsersWithGroups.filter(
      (application) =>
        application.validGroup?.isArchived === false ||
        (!application.hadGroup && application.validGroup == null)
    );
    setApplicationsCount(filteredByGroupArchive);

    const uniqueGroups = extractUniqueGroups(filteredByGroupArchive);
    setGroups(uniqueGroups);
    const isReferadaAcceptedMap = {
      accepted: true,
      declined: false,
      undecided: null,
    };
    const isReferadaAccepted = isReferadaAcceptedMap[selectedApplicationStatus];
    const filteredbyReferadaAcceptance = filteredByGroupArchive.filter(
      (app) => app.isReferadaAccepted === isReferadaAccepted
    );
    const finalFilteredApplications = filteredbyReferadaAcceptance.filter(
      (application) => {
        if (selectedGroup === "all") return true;
        return String(application.validGroup?.id) === String(selectedGroup);
      }
    );
    setUpdatedApplications(finalFilteredApplications);
  }, [
    selectedProgram,
    searchQuery,
    applications,
    selectedApplicationStatus,
    selectedGroup,
  ]);

  const statusCounts = useMemo(() => {
    if (!selectedProgram)
      return {
        acceptedApplications: 0,
        declinedApplications: 0,
        undecidedApplications: 0,
      };
    const filteredByProgram =
      applicationsCount.filter((application) =>
        selectedProgram?.split(",")?.map(Number).includes(application.programId)
      ) || [];

    const acceptedApplications = filteredByProgram.filter(
      (app) => app.isReferadaAccepted === true
    ).length;
    const declinedApplications = filteredByProgram.filter(
      (app) => app.isReferadaAccepted === false
    ).length;
    const undecidedApplications = filteredByProgram.filter(
      (app) => app.isReferadaAccepted === null
    ).length;

    return {
      acceptedApplications,
      declinedApplications,
      undecidedApplications,
      filteredApplicationsCount: filteredByProgram.length,
    };
  }, [selectedProgram, applicationsCount]);

  const statusOptions = [
    {
      key: "undecided",
      label: "Neocijenjeno",
      color: "transparent",
      count: statusCounts?.undecidedApplications,
    },
    {
      key: "accepted",
      label: "Odobreno",
      color: "#E8FEF8",
      count: statusCounts?.acceptedApplications,
    },
    {
      key: "declined",
      label: "Odbijeno",
      color: "#FEE8E8",
      count: statusCounts?.declinedApplications,
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.backButton} onClick={() => navigate("/dokumenti")}>
        <ArrowLeftIcon className={styles.backButtonArrow} />
        <p className={styles.backButtonText}>natrag</p>
      </div>
      <div className={styles.flexContainer}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            className={`${styles.searchInput} ${styles.searchInputRef}`}
            placeholder="Pretraži po imenu"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        <div className={styles.selectContainer}>
          <select className={styles.select} onChange={handleSelectChange}>
            {programOptions?.map((program, index) => (
              <option value={program.value} key={index}>
                {program.label}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.selectContainer}>
          <select
            className={styles.select}
            onChange={(event) => setSelectedGroup(event.target.value)}
            defaultValue=""
          >
            <option value="" disabled hidden>
              Odaberi grupu
            </option>
            <option value="all">Sve grupe</option>

            {groups?.map((group, index) => (
              <option value={group.id} key={index}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className={styles.countContainer}>
        {statusOptions.map(({ key, label, color, count }) => {
          const statusSelected = selectedApplicationStatus === key;
          return (
            <p
              key={key}
              className={styles.label}
              style={{
                backgroundColor: statusSelected ? color : "transparent",
                borderColor: statusSelected ? "#d9dce0" : "transparent",
              }}
              onClick={() => setSelectedApplicationStatus(key)}
            >
              {label}: {count}
            </p>
          );
        })}

        <p className={styles.label} style={{ cursor: "initial" }}>
          Ukupno: {statusCounts?.filteredApplicationsCount}
        </p>
      </div>
      {updatedApplications?.length > 0 ? (
        <Table
          dataLive={updatedApplications}
          selectedProgram={selectedProgram}
          selectedApplicationStatus={selectedApplicationStatus}
        />
      ) : (
        <div className={styles.noGroupsWrapper}>
          <img alt="" src={InfoIcon} className={styles.noGroupsIcon} />
          Trenutno nema aplikacija za ovaj program.
        </div>
      )}
    </div>
  );
};

export default EnrollmentApplications;
